import { useState, useRef, useEffect, ChangeEvent, KeyboardEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Input from "../../../components/common/Input";
import Button from "../../../components/common/Button";
import { useFormDataStore } from "../../../store/formDataStore";
import { StepProps } from "../../../types/onboarding";
import styles from "../OnboardingPage.module.css";
import { useCityAutoComplete } from "../../../hooks/useCityAutoComplete";

const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 50;
const NAME_REGEX = /^[A-Za-zА-Яа-яЁё\s-]+$/; // кириллица, латиница, пробел и тире

const StepName: React.FC<StepProps> = ({ onNext }) => {
  const { formData, setFormData } = useFormDataStore();
  const { citySuggestions, showSuggestions, handleInputChange, handleCitySelect } =
    useCityAutoComplete();

  const [nameError, setNameError] = useState<string | null>(null);
  const [highlightButton, setHighlightButton] = useState(false);

  const nameInputRef = useRef<HTMLInputElement>(null);
  const cityInputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const validateName = (value: string): string | null => {
    if (!value.trim()) return "Обязательное поле";
    if (!NAME_REGEX.test(value))
      return "Допустимы только буквы латинского алфавита и кириллицы, пробел и тире";
    if (value.length < MIN_NAME_LENGTH || value.length > MAX_NAME_LENGTH) {
      return `Имя должно быть от ${MIN_NAME_LENGTH} до ${MAX_NAME_LENGTH} символов`;
    }
    return null;
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData({ name: value });
    setNameError(validateName(value));
  };

  const handleNameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      nameInputRef.current?.blur();
      cityInputRef.current?.focus();
    }
  };

  useEffect(() => {
    if (formData.cityNameForApi && formData.name && !nameError) {
      buttonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightButton(true);

      const timer = setTimeout(() => setHighlightButton(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [formData.cityNameForApi, formData.name, nameError]);

  return (
    <motion.div
      key="step-name"
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -100 }}
    >
      <h2>Ваше имя и место рождения</h2>
      <p>Введите имя и место рождения для персонализации прогноза.</p>

      <div className={styles.fieldGroup}>
        <Input
          ref={nameInputRef}
          value={formData.name}
          onChange={handleNameChange}
          onKeyDown={handleNameKeyDown}
          placeholder="Введите имя"
          className={nameError ? styles.invalid : undefined}
        />

        <AnimatePresence>
          {nameError && (
            <motion.div
              key="name-error"
              className={styles.errorText}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
            >
              {nameError}
            </motion.div>
          )}
        </AnimatePresence>

        <div style={{ position: "relative" }}>
          <Input
            ref={cityInputRef}
            value={formData.placeOfBirth}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder="Место рождения (город, страна)"
          />
          {showSuggestions && citySuggestions.length > 0 && (
            <ul className={styles.suggestionList}>
              {citySuggestions.map((city, idx) => (
                <li
                  key={idx}
                  className={styles.suggestionItem}
                  onClick={() => handleCitySelect(city)}
                >
                  {city}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className={styles["button-row"]}>
        <Button
          ref={buttonRef}
          onClick={onNext}
          disabled={!formData.name || !!nameError || !formData.cityNameForApi}
          className={highlightButton ? styles.highlight : ""}
        >
          Далее
        </Button>
      </div>
    </motion.div>
  );
};

export default StepName;
